import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Check, ArrowRight, SignOut, ShieldCheck, Lightning, HandTap } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Subscribe() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("self_serve");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.subscription_status === "active") { navigate("/dashboard", { replace: true }); return; }
    api.get("/subscription/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, [user, navigate]);

  const subscribe = async (planId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan: planId });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <img src="/citehub-logo.png" alt="Cite-Hub" className="h-9 w-auto" />
          <button onClick={logout} data-testid="subscribe-logout-button" className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0055FF]">Choose your plan</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mt-3 leading-[1.05]">
            Do it yourself, or let us do it for you.
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Hi {user?.name || "there"} — pick how you want to get listed across 138 US directories.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto items-start">
          {plans.map((plan) => {
            const active = selected === plan.id;
            const isAuto = plan.done_for_you;
            const recommended = plan.id === "self_serve";
            return (
              <div key={plan.id} onClick={() => setSelected(plan.id)} data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-2xl border-2 p-8 cursor-pointer transition-all ${active ? "border-[#0055FF] shadow-lg" : "border-slate-200 hover:border-slate-300"} ${!recommended && !active ? "opacity-95" : ""}`}>
                {recommended && (
                  <span className="absolute -top-3 left-8 rounded-full bg-[#0055FF] text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1">
                    Recommended
                  </span>
                )}
                {isAuto && (
                  <span className="absolute -top-3 left-8 rounded-full bg-slate-900 text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1">
                    Done for you
                  </span>
                )}
                <div className="flex items-center gap-2 text-[#0055FF]">
                  {isAuto ? <Lightning weight="fill" size={18} /> : <HandTap weight="duotone" size={18} />}
                  <span className="text-xs font-bold uppercase tracking-[0.12em]">{plan.name}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>

                <div className="mt-5 flex items-end gap-2">
                  <span className="font-display text-5xl font-black tracking-tighter text-slate-900">${plan.monthly_amount}</span>
                  <span className="text-slate-500 mb-1.5 text-sm font-semibold">/month</span>
                </div>
                {plan.setup_amount > 0 ? (
                  <p className="mt-1.5 text-sm text-slate-600">First month <span className="font-bold text-slate-900">${plan.first_invoice}</span> (includes one-time setup), then ${plan.monthly_amount}/mo.</p>
                ) : (
                  <p className="mt-1.5 text-sm text-slate-600">Flat <span className="font-bold text-slate-900">${plan.monthly_amount}/month</span>. No setup fee.</p>
                )}

                <div className="my-6 h-px bg-slate-100" />
                <ul className="space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center ${recommended ? "bg-emerald-100" : "bg-blue-100"}`}>
                        <Check weight="bold" size={13} className={recommended ? "text-emerald-600" : "text-[#0055FF]"} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button onClick={(e) => { e.stopPropagation(); subscribe(plan.id); }} disabled={busy}
                  data-testid={`subscribe-${plan.id}`}
                  className={`mt-7 w-full h-12 font-semibold group ${recommended ? "bg-[#0055FF] hover:bg-[#0044CC] text-white" : "bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"}`}>
                  {busy ? "Redirecting…" : isAuto ? "Get Auto-Sync" : "Start Self-Serve"}
                  <ArrowRight size={18} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
                </Button>
                {isAuto && (
                  <p className="mt-3 text-center text-xs text-slate-400">Prefer to sit back? We handle everything.</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center space-y-1.5">
          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={15} /> Secure checkout powered by Stripe · Cancel anytime
          </p>
          <p className="text-xs text-slate-500" data-testid="promo-code-hint">
            Have a promo code? Enter it at checkout — try <span className="font-mono font-bold text-slate-700">3RDFREE</span> for your 3rd month free.
          </p>
        </div>
      </div>
    </div>
  );
}
